// DataSourceColumnModal — add or rename one column of a data source. The five
// types are the set the backend accepts; each pairs with a Lucide glyph and an
// i18n label, read from the shared columnTypes map.
//
// One component for both modes rather than a Create/Edit pair (the
// DataSourceEntryModal / DataSourceModal idiom). Passing `column` switches it to
// edit — but the two modes are NOT symmetric here, unlike its siblings:
//
// PATCH :id/columns/:columnId takes `{ columnName }` and nothing else, so a
// column's dataType is FIXED once it exists. Edit mode therefore renames only,
// and the type picker renders locked (disabled group + a `lock` labelTag, the
// treatment company-fields/CreateCompanyFieldModal gives its immutable `dbColumn`)
// rather than
// offering a change the contract cannot express. Showing the type greyed beats
// hiding it: the value stays readable and its fixedness is explicit.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useAddDataSourceColumnMutation,
  useUpdateDataSourceColumnMutation,
  type DataSourceColumn,
} from '@/api/endpoints/datasourceApi';
import { toast } from 'react-hot-toast';
import { Button, Field, Icon, Input, Modal } from '@/components/common';
import { COLUMN_TYPES } from '../shared/columnTypes';

interface DataSourceColumnModalProps {
  open: boolean;
  onClose: () => void;
  dataSourceId: string;
  /** Present → rename that column; absent → add a new one. */
  column?: DataSourceColumn;
}

const emptyDraft = {
  columnName: '',
  dataType: '',
};

const DataSourceColumnModal = ({
  open,
  onClose,
  dataSourceId,
  column,
}: DataSourceColumnModalProps) => {
  const { t } = useTranslation();
  const [addColumn, { isLoading: creating }] = useAddDataSourceColumnMutation();
  const [updateColumn, { isLoading: updating }] = useUpdateDataSourceColumnMutation();
  const [draft, setDraft] = useState(emptyDraft);
  const [submitted, setSubmitted] = useState(false);

  const editing = !!column;
  const saving = creating || updating;

  // Re-seed each time the modal opens: the column's current name and type in
  // edit mode, empty in create. Discards leftovers from a previous open either
  // way. The seeded dataType is display-only — it is never submitted.
  useEffect(() => {
    if (open) {
      setDraft(column ? { columnName: column.columnName, dataType: column.dataType } : emptyDraft);
      setSubmitted(false);
    }
  }, [open, column]);

  const patch = (changes: Partial<typeof emptyDraft>) => setDraft((d) => ({ ...d, ...changes }));

  // Test the SAME trimmed value that gets submitted (see DataSourceModal).
  const columnNameMissing = !draft.columnName.trim();
  // Only the create path sends a type, so only it can be missing one.
  const dataTypeMissing = !editing && !draft.dataType;

  const submit = async () => {
    // Required-field validation runs BEFORE any backend call: mark the form as
    // submitted so errors show, and bail while anything is invalid.
    setSubmitted(true);
    if (columnNameMissing || dataTypeMissing) return;
    const columnName = draft.columnName.trim();
    try {
      if (column) {
        await updateColumn({ id: dataSourceId, columnId: column.columnId, columnName }).unwrap();
      } else {
        await addColumn({ id: dataSourceId, columnName, dataType: draft.dataType }).unwrap();
      }
      toast.success(
        t(editing ? 'datasource_column_updated' : 'datasource_column_created', {
          name: columnName,
        }),
      );
      onClose();
    } catch {
      toast.error(t('error_generic'));
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      icon={editing ? 'square-pen' : 'columns-3'}
      title={t(editing ? 'datasource_column_edit_title' : 'datasource_column_create_title')}
      subtitle={t(
        editing ? 'datasource_column_edit_subtitle' : 'datasource_column_create_subtitle',
      )}
      footer={
        <>
          <Button variant='secondary' onClick={onClose} disabled={saving}>
            {t('button_cancel')}
          </Button>
          <Button iconStart={editing ? 'check' : 'plus'} onClick={submit} loading={saving}>
            {t(editing ? 'datasource_column_save' : 'datasource_column_create')}
          </Button>
        </>
      }
    >
      <div className='datasource-create'>
        <Field
          label={t('datasource_column_field_name')}
          required
          error={submitted && columnNameMissing ? t('datasource_column_name_required') : undefined}
        >
          <Input
            value={draft.columnName}
            placeholder={t('datasource_column_field_name_placeholder')}
            error={submitted && columnNameMissing}
            onChange={(e) => patch({ columnName: e.target.value })}
          />
        </Field>
        <Field
          label={t('datasource_column_field_type')}
          required={!editing}
          labelTag={
            editing ? (
              <>
                <Icon name='lock' size={13} aria-hidden='true' />
                {t('datasource_column_type_locked')}
              </>
            ) : undefined
          }
          error={submitted && dataTypeMissing ? t('datasource_column_type_required') : undefined}
        >
          <div
            className={['dscol-types', editing && 'dscol-types--locked'].filter(Boolean).join(' ')}
            role='radiogroup'
            aria-disabled={editing || undefined}
          >
            {COLUMN_TYPES.map((type) => {
              const active = draft.dataType === type.value;
              return (
                <button
                  key={type.value}
                  type='button'
                  role='radio'
                  aria-checked={active}
                  disabled={editing}
                  className={['dscol-type', active && 'dscol-type--active']
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => patch({ dataType: type.value })}
                >
                  <span className='dscol-type__icon'>
                    <Icon name={type.icon} size={20} />
                  </span>
                  <span className='dscol-type__text'>
                    <span className='dscol-type__title'>
                      {t(`datasource_column_type_${type.value}`)}
                    </span>
                    <span className='dscol-type__hint'>
                      {t(`datasource_column_type_${type.value}_hint`)}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </Field>
      </div>
    </Modal>
  );
};

export default DataSourceColumnModal;
